import { createFeatureSelector, createSelector } from "@ngrx/store";
import { State } from "./settings.reducers";
import { LessonRatingDto } from "src/app/lessons/interfaces/lesson_rating";

export const selectFeature = createFeatureSelector<State>("setting");

export const selectIsLoaded = createSelector(
  selectFeature,
  (state) => state.isLoaded
);

export const selectAllEducationCategories = createSelector(
  selectFeature,
  (state) => (state.educationCategories ? state.educationCategories : [])
);

export const selectEdcuationCategoryByCategory = (category: string) =>
  createSelector(selectFeature, (state) => {
    const educationCategory = state.educationCategories.find(
      (educationCategory) => educationCategory.category === category
    );

    if (educationCategory) {
      return {
        ...educationCategory,
        education_title: state.educationTitles.filter(
          (educationTitle) => educationTitle.education_category_id === educationCategory.education_category_id
        ).map((educationTitle) => ({
          ...educationTitle,
          education_body: state.educationBodies.filter(
            (educationBody) => educationBody.education_title_id === educationTitle.education_title_id
          )
        }))
      };
    }

    return null;
  });

export const selectEducationCategoryById = (id: number) =>
  createSelector(selectFeature, (state) => {
    return state.educationCategories.find(
      (educationCategory) => educationCategory.education_category_id === id
    );
  });

// for selectSelectedEducationCategory we return the educationCategory and not the id
export const selectSelectedEducationCategory = createSelector(
  selectFeature,
  (state) => {
    return state.educationCategories.find(
      (educationCategory) =>
        educationCategory.education_category_id ===
        state.selectedEducationCategory
    );
  }
);

export const selectSelectedEducationTitle = createSelector(
  selectFeature,
  (state) => {
    return state.educationTitles.find(
      (educationTitle) =>
        educationTitle.education_title_id === state.selectedEducationTitle
    );
  }
);

export const selectEducationTitlesByCategoryId = (id: number) =>
  createSelector(selectFeature, (state) => {
    return state.educationTitles?.filter(
      (educationTitle) => educationTitle.education_category_id === id
    );
  });

export const selectEducationBodiesByTitleId = (id: number) =>
  createSelector(selectFeature, (state) => {
    return state.educationBodies?.filter(
      (educationBody) => educationBody.education_title_id === id
    );
  });

export const selectAllLessonRatings = createSelector(selectFeature, (state) =>
  state.lessonRatings ? state.lessonRatings : []
);

export const selectLessonRatingsForLesson = (lessonId: number) =>
  createSelector(selectFeature, (state) => {
    return state.lessonRatings?.filter(
      (lessonRating) => lessonRating.appointment_id === lessonId
    );
  });

export const selectHasLessonRatingsForLesson = (lessonId: number) =>
  createSelector(selectFeature, (state) => {
    return (
      state.lessonRatings?.filter(
        (lessonRating) => lessonRating.appointment_id === lessonId
      ).length > 0
    );
  });

export const selectLessonRatingsForStudent = (studentId: number) =>
  createSelector(selectFeature, (state) => {
    return state.lessonRatings?.filter(
      (lessonRating) => lessonRating.student_id === studentId
    );
  });

export const selectLastEducationBodyLessonRatingsForStudent = (
  studentId: number
) =>
  createSelector(selectFeature, (state) => {
    const studentLessonRatings = state.lessonRatings?.filter(
      (lessonRating) => lessonRating.student_id === studentId
    );

    const lastEducationBodyLessonRatings: {
      [educationBodyId: number]: LessonRatingDto;
    } = {};

    // Iterate through filtered lesson ratings and keep only the latest one for each education_body_id
    studentLessonRatings.forEach((lessonRating) => {
      const educationBodyId = lessonRating.education_body_id;
      if (!lessonRating.rating) return;
      if (
        !lastEducationBodyLessonRatings[educationBodyId] ||
        new Date(lessonRating.created_at || "") >
          new Date(
            lastEducationBodyLessonRatings[educationBodyId].created_at || ""
          )
      ) {
        lastEducationBodyLessonRatings[educationBodyId] = lessonRating;
      }
    });

    return Object.values(lastEducationBodyLessonRatings);
  });

export const selectServices = createSelector(selectFeature, (state) =>
  state.services ? state.services : []
);

export const selectServicesByCategory = (category: string) =>
  createSelector(selectFeature, (state) => {
    return state.services.filter(
      (service) => service.category === category
    );
  });

export const selectSubscriptions = createSelector(selectFeature, (state) =>
  state.subscriptions ? state.subscriptions : []
);

export const selectSelectedSetting = createSelector(selectFeature, (state) =>
  state.selectedSetting
);

export const selectAddingLessonRatings = createSelector(selectFeature, (state) =>
  state.addingLessonRatings
);

export const selectRemovingLessonRatings = createSelector(selectFeature, (state) =>
  state.removingLessonRatings
);
